import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import SEO from "../../../utils/seo";
import { Helmet } from "react-helmet";

const CarolineSpringServiceArea = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Starlight Motors Mobile Detailing - Caroline Springs",
    "description": "Professional mobile car detailing service in Caroline Springs, VIC. Full interior & exterior detailing, ceramic coating, and paint correction delivered to your location.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Caroline Springs",
      "addressRegion": "VIC",
      "postalCode": "3023",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -37.7333,
      "longitude": 144.7333
    },
    "telephone": "0491121950",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/caroline-springs`,
    "priceRange": "$170-$600",
    "areaServed": {
      "@type": "City",
      "name": "Caroline Springs, VIC"
    }
  };

  const services = [
    {
      title: "Full Interior & Exterior Detail",
      price: "$250",
      originalPrice: "$280",
      features: ["Complete exterior wash & wax", "Interior deep clean", "Leather conditioning", "Window cleaning", "Tire shine"]
    },
    {
      title: "Interior Only Detail",
      price: "$180",
      originalPrice: "$210",
      features: ["Deep vacuum", "Leather treatment", "Dashboard cleaning", "Seat protection", "Interior windows"]
    },
    {
      title: "Exterior Only Detail",
      price: "$120",
      originalPrice: "$150",
      features: ["Exterior wash", "Paint protection wax", "Wheel cleaning", "Tire shine", "Trim restoration"]
    },
    {
      title: "Ceramic Coating",
      price: "from $600",
      features: ["Paint correction", "Premium coating", "2-5 year protection", "Enhanced gloss", "Easy maintenance"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mobile Car Detailing Caroline Springs | Starlight Motors - Quality Service</title>
        <meta name="description" content="Expert mobile car detailing in Caroline Springs, Melbourne. Starlight Motors offers professional detailing at your doorstep. Full interior & exterior detailing, ceramic coating. Call 0491121950 for Caroline Springs' trusted mobile detailing." />
        <meta name="keywords" content="mobile car detailing Caroline Springs, car detailing Caroline Springs Melbourne, ceramic coating Caroline Springs, mobile auto detailing" />
        <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/caroline-springs`} />
        <meta property="og:title" content="Mobile Car Detailing Caroline Springs | Starlight Motors" />
        <meta property="og:description" content="Expert mobile car detailing in Caroline Springs, Melbourne. Quality service at your location." />
        <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/caroline-springs`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PTMBTSXR');`}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="relative max-w-7xl mx-auto px-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                <strong>Mobile Car Detailing Caroline Springs</strong>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl">
                Expert mobile car detailing services in Caroline Springs. Professional results delivered to your driveway.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0491121950" className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2 text-xl"></i>Call 0491 121 950
                </a>
                <a href="/#contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  Get Free Quote
                </a>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-12 mb-16">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    <strong>Professional Car Detailing in Caroline Springs</strong>
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Starlight Motors is Caroline Springs' trusted mobile car detailing specialist, serving residents and businesses throughout the area. Whether you're near Caroline Springs Shopping Centre, Lake Caroline, or in the residential areas around Western Highway, we bring professional-grade car detailing services directly to your location.
                  </p>
                  <p className="text-lg text-gray-700 mb-6">
                    Our comprehensive mobile car detailing service in Caroline Springs covers everything from basic maintenance washes to premium ceramic coating applications. We understand the needs of Caroline Springs residents and deliver exceptional results while you enjoy the convenience of staying at home or work.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-3">
                        <i className="ri-map-pin-line text-white text-xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2"><strong>Local Caroline Springs Service</strong></h4>
                      <p className="text-sm text-gray-600">Trusted by Caroline Springs residents for 3+ years</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-3">
                        <i className="ri-star-line text-white text-xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2"><strong>Premium Quality</strong></h4>
                      <p className="text-sm text-gray-600">Professional-grade products and equipment</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-3">
                        <i className="ri-car-line text-white text-xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2"><strong>Fully Mobile</strong></h4>
                      <p className="text-sm text-gray-600">We come to your Caroline Springs location</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-3">
                        <i className="ri-shield-check-line text-white text-xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2"><strong>Guaranteed Results</strong></h4>
                      <p className="text-sm text-gray-600">100% satisfaction guarantee</p>
                    </div>
                  </div>
                </div>
                <div>
                  <img 
                    src="https://public.readdy.ai/ai/img_res/4e0bd58892de071ad44dd8e911f6f6c2.jpg"
                    alt="Mobile car detailing in Caroline Springs"
                    title="Professional car detailing Caroline Springs - Starlight Motors"
                    className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
                  />
                </div>
              </div>

              <div className="mb-16">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                  <strong>Caroline Springs Car Detailing Services</strong>
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {services.map((service, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-600 hover:shadow-lg transition-all">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-lg font-bold text-gray-900"><strong>{service.title}</strong></h4>
                        <div className="text-right ml-2">
                          {service.originalPrice && (
                            <div className="text-xs text-gray-400 line-through">{service.originalPrice}</div>
                          )}
                          <span className="text-blue-600 font-bold text-lg whitespace-nowrap">{service.price}</span>
                        </div>
                      </div>
                      {service.originalPrice && (
                        <div className="mb-3 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                          Save $30!
                        </div>
                      )}
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0 mt-0.5"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12 text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  <strong>Complete Coverage Across Caroline Springs, VIC 3023</strong>
                </h3>
                <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
                  We service all areas of Caroline Springs including residential neighborhoods near Caroline Springs Shopping Centre, Lake Caroline, Western Highway, and surrounding streets. Our mobile car detailing service brings professional automotive care directly to your Caroline Springs driveway, eliminating the need to visit a car wash and saving you valuable time.
                </p>
                <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
                  From routine maintenance details to premium ceramic coating protection, Starlight Motors is Caroline Springs' trusted partner for mobile car detailing. Experience the convenience of professional car care without leaving home. Book your mobile car detailing service today and discover why Caroline Springs residents choose Starlight Motors for exceptional automotive care.
                </p>
                <a href="/#contact" className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  Book Caroline Springs Detailing
                </a>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CarolineSpringServiceArea;
